import React from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Instagram, Music2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { CONTACT } from "../lib/site";

const Contact = () => {
  const { t } = useLang();
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-36 bg-black border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3 mb-6"
            >
              <span className="h-px w-10 bg-gold" />
              <span className="label-eyebrow text-gold">{t.contact.eyebrow}</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="font-display uppercase text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-10"
            >
              {t.contact.title}
            </motion.h2>

            <div className="space-y-6">
              {[
                {
                  icon: MapPin,
                  label: "Address",
                  value: t.contact.address,
                  href: `https://maps.google.com/?q=${encodeURIComponent("Kivimaantie 19, Ii, Finland")}`,
                  testid: "contact-address",
                },
                {
                  icon: Phone,
                  label: t.contact.phone,
                  value: CONTACT.phone,
                  href: CONTACT.phoneHref,
                  testid: "contact-phone",
                },
                {
                  icon: Mail,
                  label: t.contact.email,
                  value: CONTACT.email,
                  href: `mailto:${CONTACT.email}`,
                  testid: "contact-email",
                },
                {
                  icon: Clock,
                  label: t.contact.hours,
                  value: t.contact.hoursValue,
                  testid: "contact-hours",
                },
              ].map((row, i) => {
                const Icon = row.icon;
                const Body = (
                  <>
                    <div className="h-10 w-10 rounded-sm border border-white/10 group-hover:border-gold/50 flex items-center justify-center transition-colors">
                      <Icon className="h-4 w-4 text-gold" strokeWidth={1.8} />
                    </div>
                    <div>
                      <div className="label-eyebrow text-white/40">{row.label}</div>
                      <div className="text-white mt-1">{row.value}</div>
                    </div>
                  </>
                );
                return row.href ? (
                  <a
                    key={i}
                    href={row.href}
                    target={row.href.startsWith("http") ? "_blank" : undefined}
                    rel="noreferrer"
                    className="flex items-start gap-4 group"
                    data-testid={row.testid}
                  >
                    {Body}
                  </a>
                ) : (
                  <div key={i} className="flex items-start gap-4 group" data-testid={row.testid}>
                    {Body}
                  </div>
                );
              })}
            </div>

            <div className="mt-10 pt-8 border-t border-white/10">
              <div className="label-eyebrow text-white/40 mb-4">{t.contact.socials}</div>
              <div className="flex gap-3">
                <a
                  href={CONTACT.instagram}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="social-instagram"
                  className="group flex items-center gap-2 border border-white/10 hover:border-gold/50 hover:bg-gold/5 px-4 py-2.5 rounded-sm transition-all"
                >
                  <Instagram className="h-4 w-4 text-gold" strokeWidth={1.8} />
                  <span className="text-sm">{CONTACT.instagramHandle}</span>
                </a>
                <a
                  href={CONTACT.tiktok}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="social-tiktok"
                  className="group flex items-center gap-2 border border-white/10 hover:border-gold/50 hover:bg-gold/5 px-4 py-2.5 rounded-sm transition-all"
                >
                  <Music2 className="h-4 w-4 text-gold" strokeWidth={1.8} />
                  <span className="text-sm">{CONTACT.tiktokHandle}</span>
                </a>
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="relative aspect-[4/3] md:aspect-auto md:h-full min-h-[420px] border border-white/10 rounded-sm overflow-hidden">
              <iframe
                title="TTcuts location"
                src={CONTACT.mapsEmbed}
                className="absolute inset-0 w-full h-full grayscale contrast-125 brightness-75"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="map-iframe"
              />
              <div className="absolute top-4 left-4 bg-black/80 backdrop-blur border border-gold/30 px-4 py-2.5 rounded-sm">
                <div className="label-eyebrow text-gold text-[10px]">TTcuts</div>
                <div className="text-xs text-white/80">Kivimaantie 19, Ii</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
